/**
 * Runtime safety net for contradictory layout tokens emitted by generated UI.
 *
 * A vertical spacing contract (`space-y-*`) describes a column. A bare `flex`
 * on the same node changes it back to a row and causes the overlap/collapse
 * seen in generated dashboards, articles, and product cards. Likewise, the
 * literal `undefined` token is generator debris and must never reach the DOM.
 *
 * Generated shells also carried three deterministic contradictions: divided
 * table bodies laid out as rows, full-width left navigation groups competing
 * with right-side actions, and user metadata groups without a display mode.
 * Those contracts are repaired by semantic IDs/classes while intentional
 * responsive rows remain untouched.
 */

export interface GeneratedLayoutUiNode {
  id?: string;
  className?: string;
  children?: GeneratedLayoutUiNode[];
  [key: string]: unknown;
}

export interface GeneratedLayoutNormalizationStats {
  verticalStackRewrites: number;
  shellConstraintRewrites: number;
  legacyAppDataRewrites: number;
  invalidTokenRemovals: number;
}

const VERTICAL_SPACE_RE = /^(?:[a-z]+:)*space-y-/;
const DIVIDE_Y_RE = /^(?:[a-z]+:)*divide-y(?:-|$)/;

function normalizeClassName(
  node: GeneratedLayoutUiNode,
  forceVerticalStack: boolean,
): {
  className: string;
  verticalStackRewrite: boolean;
  shellConstraintRewrite: boolean;
  invalidTokenRemoval: boolean;
} {
  const className = node.className ?? "";
  const tokens = className.split(/\s+/).filter(Boolean);
  const invalidTokenRemoval = tokens.includes("undefined");
  let withoutInvalid = tokens.filter((token) => token !== "undefined");
  const hasVerticalSpace = withoutInvalid.some((token) =>
    VERTICAL_SPACE_RE.test(token),
  );
  const hasVerticalDivide = withoutInvalid.some((token) =>
    DIVIDE_Y_RE.test(token),
  );
  const hasBareFlex = withoutInvalid.includes("flex");
  const hasColumn = withoutInvalid.some((token) =>
    /^(?:[a-z]+:)*flex-col$/.test(token),
  );
  const verticalStackRewrite =
    (hasVerticalSpace || hasVerticalDivide || forceVerticalStack) &&
    hasBareFlex &&
    !hasColumn;
  let shellConstraintRewrite = false;

  if (verticalStackRewrite) {
    const layoutTokens = withoutInvalid.filter(
      (token) =>
        token !== "items-center" &&
        token !== "items-end" &&
        token !== "items-baseline",
    );
    const flexIndex = layoutTokens.indexOf("flex");
    layoutTokens.splice(flexIndex + 1, 0, "flex-col");
    withoutInvalid = layoutTokens;
  }

  if (
    node.id === "left-section" &&
    withoutInvalid.includes("flex") &&
    withoutInvalid.includes("w-full")
  ) {
    withoutInvalid = withoutInvalid.filter((token) => token !== "w-full");
    if (!withoutInvalid.includes("flex-1")) withoutInvalid.push("flex-1");
    if (!withoutInvalid.includes("min-w-0")) withoutInvalid.push("min-w-0");
    shellConstraintRewrite = true;
  }

  if (
    node.id === "user-info" &&
    !withoutInvalid.some((token) =>
      /^(?:[a-z]+:)*(?:flex|grid|block|inline-flex)$/.test(token),
    )
  ) {
    withoutInvalid = withoutInvalid.filter(
      (token) => token !== "items-center" && token !== "items-end",
    );
    withoutInvalid.push("flex", "flex-col", "items-start");
    shellConstraintRewrite = true;
  }

  return {
    className: withoutInvalid.join(" "),
    verticalStackRewrite,
    shellConstraintRewrite,
    invalidTokenRemoval,
  };
}

export function normalizeGeneratedLayout(
  root: GeneratedLayoutUiNode | null | undefined,
): GeneratedLayoutNormalizationStats {
  const stats: GeneratedLayoutNormalizationStats = {
    verticalStackRewrites: 0,
    shellConstraintRewrites: 0,
    legacyAppDataRewrites: 0,
    invalidTokenRemovals: 0,
  };
  if (!root || typeof root !== "object") return stats;

  const walk = (node: GeneratedLayoutUiNode) => {
    for (const [key, value] of Object.entries(node)) {
      if (
        key !== "className" &&
        key !== "children" &&
        typeof value === "string" &&
        value.includes("__app.")
      ) {
        try {
          node[key] = value.replace(/\b__app\./g, "appData.");
          stats.legacyAppDataRewrites++;
        } catch {
          // Frozen imported fixtures are left unchanged.
        }
      }
    }
    if (typeof node.className === "string") {
      const forceVerticalStack =
        Array.isArray(node.children) &&
        node.children.some(
          (child) =>
            typeof child.className === "string" &&
            child.className
              .split(/\s+/)
              .some((token) => DIVIDE_Y_RE.test(token)),
        );
      const next = normalizeClassName(node, forceVerticalStack);
      if (next.className !== node.className) {
        try {
          node.className = next.className;
          if (next.verticalStackRewrite) stats.verticalStackRewrites++;
          if (next.shellConstraintRewrite) stats.shellConstraintRewrites++;
          if (next.invalidTokenRemoval) stats.invalidTokenRemovals++;
        } catch {
          // Imported fixture JSON can be frozen by the bundler. Source remains
          // renderable; the normalizer simply cannot repair that one node.
        }
      }
    }
    if (Array.isArray(node.children)) node.children.forEach(walk);
  };

  walk(root);
  return stats;
}
